import type { AuthMethodV1, AuthV1, CreditV1, DocRevisionV1, DocSharingV1, DocTemplateV1, DocumentUnlockSettingsV1, DocumentV1, IdentityV1, MagicLinkRequestBody, PaymentV1, ProductSupportV1, VoucherV1 } from '@alpod-ai-writer/backend';
import type { JobQueueV1 } from '@alpod-ai-writer/backend/src/schemas/jobQueue/jobQueue.schema';
import type { BillingResidency } from '@alpod-ai-writer/shared';

import type { ApiClient, TypedApiClient } from './apiClient';

export const createApi = (client: TypedApiClient) => ({
    client: client as ApiClient,
    v1: {
        auth: {
            getProviders: () =>
                client.get<AuthMethodV1.ProviderList>('/v1/auth/providers', {}),
            refreshByCookie: () =>
                client.post<null, AuthV1.Tokens>('/v1/auth/token/refreshByCookie', {
                    body: null,
                    transformResponse: (res) => ({
                        ...res,
                        accessTokenExpiresAt: new Date(res.accessTokenExpiresAt)
                    })
                }),
            getGoogleLoginUrl: () =>
                client.post<null, { authUrl: string }>('/v1/auth/google/login-url', { body: null }),
            getGoogleConnectUrl: () =>
                client.post<null, { authUrl: string }>('/v1/auth/google/connect-url', { body: null }),
            provider: {
                magicLink: {
                    sendMagicLink: (email: string, destination?: string, tacAccepted?: boolean, gdprAccepted?: boolean) =>
                        client.post<MagicLinkRequestBody, AuthV1.ResponseAuthenticated>('/v1/auth/provider/magicLink/request', {
                            body: {
                                action: 'sendMagicLink',
                                email,
                                destination,
                                tacAccepted,
                                gdprAccepted
                            }
                        }),
                    verifyMagicLink: (token: string) =>
                        client.post<MagicLinkRequestBody, AuthV1.ResponseAuthenticated>('/v1/auth/provider/magicLink/request', {
                            body: {
                                action: 'verifyMagicLink',
                                token
                            },
                            query: {
                                useCookieRefreshToken: true
                            },
                            transformResponse: (res) => ({
                                ...res,
                                tokens: {
                                    ...res.tokens,
                                    accessTokenExpiresAt: new Date(res.tokens.accessTokenExpiresAt)
                                }
                            })
                        }),
                },
                password: {
                    login: (email: string, password: string) =>
                        client.post<{ email: string; password: string }, AuthV1.ResponseAuthenticated>('/v1/auth/provider/password/request', {
                            body: { email, password },
                            query: { useCookieRefreshToken: true },
                            transformResponse: (res) => ({
                                ...res,
                                tokens: {
                                    ...res.tokens,
                                    accessTokenExpiresAt: new Date(res.tokens.accessTokenExpiresAt)
                                }
                            })
                        }),
                    register: (email: string, password: string, name: string) =>
                        client.post<{ email: string; password: string; name: string }, AuthV1.ResponseAuthenticated>('/v1/auth/provider/password/request', {
                            body: { email, password, name },
                            query: { useCookieRefreshToken: true },
                            transformResponse: (res) => ({
                                ...res,
                                tokens: {
                                    ...res.tokens,
                                    accessTokenExpiresAt: new Date(res.tokens.accessTokenExpiresAt)
                                }
                            })
                        }),
                    resetRequest: (email: string) =>
                        client.post<{ email: string }, { ok: boolean }>('/v1/password/reset-request', {
                            body: { email }
                        }),
                    resetConfirm: (token: string, newPassword: string) =>
                        client.post<{ token: string; newPassword: string }, { ok: boolean }>('/v1/password/reset-confirm', {
                            body: { token, newPassword }
                        }),
                    setInitialPassword: (password: string) =>
                        client.post<{ password: string }, { ok: boolean }>('/v1/password/set-initial', {
                            body: { password }
                        }),
                    changePassword: (currentPassword: string, newPassword: string) =>
                        client.post<{ currentPassword: string; newPassword: string }, { ok: boolean }>('/v1/password/change', {
                            body: { currentPassword, newPassword }
                        }),

                },

            }
        },
        identities: {
            getMyIdentity: () =>
                client.get<IdentityV1.OwnRecord>('/v1/identities/me', {}),
            getMyAuthMethods: () =>
                client.get<AuthMethodV1.List>('/v1/identities/me/authMethods', {}),
            disconnectAuthMethod: (providerName: string, accountKey: string) =>
                client.delete<void>(`/v1/identities/me/authMethods/id/${providerName}/:accountKey`, {
                    params: { accountKey }
                }),
            updateMyIdentity: (data: IdentityV1.OwnUpdate) =>
                client.put<IdentityV1.OwnUpdate, IdentityV1.OwnRecord>('/v1/identities/me', {
                    body: data
                }),
            revokeMySession: (sessionId: string) =>
                client.post<null, void>('/v1/identities/me/authSessions/id/:sessionId/revoke', {
                    params: { sessionId },
                    body: null
                }),
        },
        documents: {
            getMy: (query?: DocumentV1.ListQuery) =>
                client.get<DocumentV1.List>('/v1/documents/my', {
                    query
                }),
            getSharedWithMeDocumentList: (query?: DocumentV1.ListQuery) =>
                client.get<DocumentV1.List>('/v1/documents/sharedWithMe', {
                    query
                }),
            create: (data: DocumentV1.Create) =>
                client.post<DocumentV1.Create, DocumentV1.Record>('/v1/documents', {
                    body: data
                }),
            getById: (id: string) =>
                client.get<DocumentV1.Record>('/v1/documents/id/:id', {
                    params: { id }
                }),
            updateById: (id: string, data: DocumentV1.Update) =>
                client.put<DocumentV1.Update, DocumentV1.Record>('/v1/documents/id/:id', {
                    params: { id },
                    body: data
                }),
            deleteById: (id: string) =>
                client.delete<void>('/v1/documents/id/:id', {
                    params: { id }
                }),
            getSharingList: (documentId: string) =>
                client.get<DocSharingV1.List>('/v1/documents/id/:id/share', {
                    params: { id: documentId }
                }),
            addSharing: (documentId: string, data: DocSharingV1.Create) =>
                client.post<DocSharingV1.Create, undefined>('/v1/documents/id/:id/share', {
                    params: { id: documentId },
                    body: data
                }),
            updateSharing: (documentId: string, userId: string, data: DocSharingV1.Update) =>
                client.put<DocSharingV1.Update, undefined>('/v1/documents/id/:id/share/:userId', {
                    params: { id: documentId, userId },
                    body: data
                }),
            deleteSharing: (documentId: string, userId: string) =>
                client.delete<void>('/v1/documents/id/:id/share/:userId', {
                    params: { id: documentId, userId }
                }),
            getRevisionList: (documentId: string, query?: Partial<DocRevisionV1.ListQuery>) =>
                client.get<DocRevisionV1.List>('/v1/documents/id/:id/revisions', {
                    params: { id: documentId },
                    query
                }),
            getRevisionById: (documentId: string, revisionId: string) =>
                client.get<DocRevisionV1.Record>('/v1/documents/id/:documentId/revisions/id/:revisionId', {
                    params: { documentId, revisionId }
                }),
            setRevisionAsMain: (documentId: string, revisionId: string) =>
                client.post<null, void>('/v1/documents/id/:documentId/revisions/id/:revisionId/setAsMain', {
                    params: { documentId, revisionId },
                    body: null
                }),
            getMediaUploadLink: (documentId: string, req: DocumentV1.UploadLinkRequest) =>
                client.post<DocumentV1.UploadLinkRequest, DocumentV1.UploadLinkResponse>('/v1/documents/id/:id/getMediaUploadLink', {
                    params: { id: documentId },
                    body: req
                }),
            getInvoiceUrl: (documentId: string) =>
                client.get<PaymentV1.GetInvoiceUrlResponse>('/v1/documents/id/:id/invoiceUrl', {
                    params: { id: documentId }
                }),
            getBankTransferQr: (documentId: string, residency: BillingResidency) =>
                client.get<PaymentV1.BankTransferQrResponse>('/v1/documents/id/:id/bankTransferQr', {
                    params: { id: documentId },
                    query: { residency }
                }),
            payWithGoPay: (documentId: string, req: PaymentV1.GoPayPaymentRequest) =>
                client.post<PaymentV1.GoPayPaymentRequest, PaymentV1.GoPayPaymentLink>('/v1/documents/id/:id/payWithGoPay', {
                    params: { id: documentId },
                    body: req
                }),
            verifyGoPayPaymentState: (documentId: string) =>
                client.post<undefined, PaymentV1.GoPayPaymentState>('/v1/documents/id/:id/verifyGoPayPaymentState', {
                    params: { id: documentId },
                    body: undefined
                }),
            payWithGpWebpay: (documentId: string, req: PaymentV1.GpWebpayPaymentRequest) =>
                client.post<PaymentV1.GpWebpayPaymentRequest, PaymentV1.GpWebpayPaymentLink>('/v1/documents/id/:id/payWithGpWebpay', {
                    params: { id: documentId },
                    body: req
                }),
            getGpWebpayPaymentState: (documentId: string) =>
                client.get<PaymentV1.GpWebpayPaymentState>('/v1/documents/id/:id/gpWebpayPaymentState', {
                    params: { id: documentId }
                }),
            aiProofread: (documentId: string, req: DocumentV1.AiProofreadingRequest) =>
                client.post<DocumentV1.AiProofreadingRequest, JobQueueV1.Record>('/v1/documents/id/:id/ai/proofread', {
                    params: { id: documentId },
                    body: req
                }),
            getJobList: (documentId: string, query?: JobQueueV1.ListQuery) =>
                client.get<JobQueueV1.List>('/v1/documents/id/:id/jobs', {
                    params: { id: documentId },
                    query
                }),
            getJobById: (documentId: string, jobId: string) =>
                client.get<JobQueueV1.Record>('/v1/documents/id/:id/jobs/id/:jobId', {
                    params: { id: documentId, jobId }
                }),
            cancelJobById: (documentId: string, jobId: string) =>
                client.post<null, void>('/v1/documents/id/:id/jobs/id/:jobId/cancel', {
                    params: { id: documentId, jobId },
                    body: null
                }),
            dismissJobById: (documentId: string, jobId: string) =>
                client.post<null, void>('/v1/documents/id/:id/jobs/id/:jobId/dismiss', {
                    params: { id: documentId, jobId },
                    body: null
                }),
        },
        templates: {
            getAll: (query?: DocTemplateV1.ListQuery) => {
                const publishedFilter = ['isPublished', 'eq', true];
                const filter = query?.filter
                    ? ['and', [query.filter, publishedFilter]]
                    : publishedFilter;
                return client.get<DocTemplateV1.List>('/v1/templates', {
                    query: { ...query, filter: filter as unknown as DocTemplateV1.ListQuery['filter'] }
                });
            },
            getById: (templateId: string) =>
                client.get<DocTemplateV1.Record>('/v1/templates/id/:id', {
                    params: { id: templateId }
                })
        },
        config: {
            getPaymentConfig: () =>
                client.get<PaymentV1.Config>('/v1/config/payment', {}),
            getDocumentUnlockSettings: () =>
                client.get<DocumentUnlockSettingsV1.Record>('/v1/document-unlock-settings/', {}),
        },
        productSupport: {
            submitFeedback: (data: ProductSupportV1.SubmitFeedback) =>
                client.post<ProductSupportV1.SubmitFeedback, undefined>('/v1/productSupport/submitFeedback', {
                    body: data
                })
        },
        credits: {
            // Get user's wallet balance
            getBalance: () =>
                client.get<number>('/v1/credits/getMyCreditBalance', {}),

            getFullBalance: () =>
                client.get<CreditV1.Balance>('/v1/credits/getBalance', {}),

            // Get credit transaction history
            getTransactions: (query?: CreditV1.ListQuery) =>
                client.get<CreditV1.List>('/v1/credits/getMyTransactions', {
                    query
                }),

            // Create credit transaction
            createTransaction: (data: CreditV1.ChargeCreditsRequest) =>
                client.post<CreditV1.ChargeCreditsRequest, number>('/v1/credits/createCreditTransaction', {
                    body: data
                }),

            getPackages: () =>
                client.get<CreditV1.PackageList>('/v1/credits/packages/all-active', {}),

            // Purchase credits via GP WebPay
            purchaseWithGpWebpay: (data: PaymentV1.CreditPurchaseRequest) =>
                client.post<PaymentV1.CreditPurchaseRequest, PaymentV1.CreditPurchasePaymentLink>('/v1/credits/purchaseWithGpWebpay', {
                    body: data
                }),

            // Purchase credits via GoPay
            purchaseWithGoPay: (data: PaymentV1.CreditPurchaseRequest) =>
                client.post<PaymentV1.CreditPurchaseRequest, PaymentV1.CreditPurchasePaymentLink>('/v1/credits/purchaseWithGoPay', {
                    body: data
                }),

            // Purchase credits via bank transfer
            purchaseWithBankTransfer: (data: PaymentV1.CreditPurchaseRequest) =>
                client.post<PaymentV1.CreditPurchaseRequest, PaymentV1.CreditPurchaseBankTransferResponse>('/v1/credits/purchaseWithBankTransfer', {
                    body: data
                }),

            // Get payment status for a credit purchase order (any payment method)
            getPurchaseOrderStatus: (orderId: string) =>
                client.get<PaymentV1.CreditPurchaseOrderStatus>('/v1/credits/purchaseOrders/:orderId/status', {
                    params: { orderId }
                }),

            // Get GP WebPay payment status (legacy, use getPurchaseOrderStatus)
            getGpWebpayPaymentStatus: (orderId: string) =>
                client.get<PaymentV1.CreditPurchaseOrderStatus>('/v1/credits/purchaseOrders/:orderId/gpWebpayPaymentStatus', {
                    params: { orderId }
                }),

            // Get AI feature costs
            getFeatureCosts: () =>
                client.get<CreditV1.AiFeaturePriceList>('/v1/credits/feature-prices/all', {}),

        },
        vouchers: {
            getActive: () =>
                client.get<VoucherV1.List>('/v1/vouchers/all-active', {}),
            getByCode: (code: string) =>
                client.get<VoucherV1.Record>('/v1/vouchers/code/:code', {
                    params: { code }
                }),
        }
    }
});

export type ApiEndpoints = ReturnType<typeof createApi>;
